import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import vendorApi from '../services/vendorApi';

const VendorPayoutDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPayoutDetail();
  }, [id]);

  const loadPayoutDetail = async () => {
    try {
      const data = await vendorApi.getPayoutDetail(id);
      setPayout(data);
    } catch (err) {
      setError('Failed to load payout details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/vendor/payouts')}
        sx={{ mb: 2 }}
      >
        Back to Payouts
      </Button>

      <Typography variant="h4" gutterBottom>
        Payout Detail
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payout Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    ${payout.total_amount?.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Items Count
                  </Typography>
                  <Typography variant="h6">{payout.items_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Payout Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(payout.payout_date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">{payout.payment_method}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Reference Number
                  </Typography>
                  <Typography variant="body1">
                    {payout.reference_number || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip label={payout.status} color="primary" size="small" />
                </Box>
                {payout.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">{payout.notes}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Items Included ({payout.items?.length || 0})
              </Typography>
              {payout.items && payout.items.length > 0 ? (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Sale Price</TableCell>
                        <TableCell align="right">Commission</TableCell>
                        <TableCell align="right">Your Payout</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payout.items.map((consignment) => {
                        const salePrice = consignment.actual_sale_price || consignment.agreed_price;
                        const commission = salePrice * (consignment.commission_rate / 100);
                        const itemPayout = salePrice - commission;

                        return (
                          <TableRow key={consignment.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {consignment.item?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>${salePrice?.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {consignment.commission_rate}% (${commission.toFixed(2)})
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main" fontWeight="medium">
                                ${itemPayout.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No items available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VendorPayoutDetail;
